import { Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import logo from "@/assets/logo-meitoolbox.webp";
import { ChevronDown, Menu, X, Sparkles, History } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";

const featuredTools: { slug: string; label: string }[] = [
  { slug: "calculadora-das-mei", label: "Calculadora DAS" },
  { slug: "limite-faturamento-mei", label: "Limite de faturamento" },
  { slug: "calculadora-precificacao", label: "Precificação" },
  { slug: "valor-da-hora", label: "Valor da hora" },
  { slug: "margem-markup", label: "Margem & markup" },
  { slug: "validador-cnpj", label: "Validador de CNPJ" },
  { slug: "gerador-recibo", label: "Gerador de recibo" },
  { slug: "controle-entradas-saidas", label: "Controle financeiro" },
];

const featuredArticles: { slug: string; label: string }[] = [
  { slug: "o-que-e-mei", label: "O que é MEI" },
  { slug: "como-formalizar-mei", label: "Como formalizar MEI" },
  { slug: "das-boleto-mei", label: "DAS / Boleto MEI" },
  { slug: "limite-faturamento-mei", label: "Limite de faturamento" },
  { slug: "mei-pode-ter-funcionario", label: "MEI pode ter funcionário?" },
  { slug: "como-cancelar-mei-dar-baixa", label: "Cancelar / dar baixa" },
];

export function SiteHeader() {
  const [openMenu, setOpenMenu] = useState<"tools" | "articles" | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 md:bg-background/80 md:backdrop-blur-lg">
      <div className="container mx-auto flex h-16 sm:h-20 md:h-24 items-center justify-between gap-4 px-4">
        <Link to="/" className="flex items-center group shrink-0 min-w-0">
          <img
            src={logo}
            alt="MEI Toolbox — ferramentas grátis para microempreendedor individual"
            title="MEI Toolbox — Calculadora DAS, precificação e mais para o MEI"
            width={904}
            height={242}
            decoding="async"
            fetchPriority="high"
            className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto max-w-[60vw] object-contain transition-transform group-hover:scale-105 dark:brightness-0 dark:invert"
          />
        </Link>

        <nav ref={navRef} className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground transition"
            activeProps={{ className: "text-foreground font-semibold" }}
            activeOptions={{ exact: true }}
          >
            Início
          </Link>

          {/* Ferramentas dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === "tools" ? null : "tools")}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition"
            >
              Ferramentas{" "}
              <ChevronDown
                className={`h-3.5 w-3.5 transition ${openMenu === "tools" ? "rotate-180" : ""}`}
              />
            </button>
            {openMenu === "tools" && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-[480px] rounded-2xl border border-border bg-card shadow-xl p-4 z-50">
                <div className="grid grid-cols-2 gap-1">
                  {featuredTools.map((t) => (
                    <Link
                      key={t.slug}
                      to="/ferramentas/$slug"
                      params={{ slug: t.slug }}
                      onClick={() => setOpenMenu(null)}
                      className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-surface transition"
                    >
                      {t.label}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/ferramentas"
                  onClick={() => setOpenMenu(null)}
                  className="mt-3 block text-center rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 transition"
                >
                  Ver todas as ferramentas →
                </Link>
              </div>
            )}
          </div>

          {/* Artigos dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === "articles" ? null : "articles")}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition"
            >
              Artigos{" "}
              <ChevronDown
                className={`h-3.5 w-3.5 transition ${openMenu === "articles" ? "rotate-180" : ""}`}
              />
            </button>
            {openMenu === "articles" && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-[420px] rounded-2xl border border-border bg-card shadow-xl p-4 z-50">
                <div className="grid grid-cols-1 gap-1">
                  {featuredArticles.map((a) => (
                    <Link
                      key={a.slug}
                      to="/artigos/$slug"
                      params={{ slug: a.slug }}
                      onClick={() => setOpenMenu(null)}
                      className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-surface transition"
                    >
                      {a.label}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/artigos"
                  onClick={() => setOpenMenu(null)}
                  className="mt-3 block text-center rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 transition"
                >
                  Ver todos os artigos →
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/marca"
            className="text-muted-foreground hover:text-foreground transition"
            activeProps={{ className: "text-foreground font-semibold" }}
          >
            Minha marca
          </Link>
          <Link
            to="/historico"
            className="text-muted-foreground hover:text-foreground transition"
            activeProps={{ className: "text-foreground font-semibold" }}
          >
            Histórico
          </Link>
          <Link
            to="/contato"
            className="text-muted-foreground hover:text-foreground transition"
            activeProps={{ className: "text-foreground font-semibold" }}
          >
            Contato
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/marca"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-amber-400 transition"
            title="Configurar minha marca"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Minha marca
          </Link>
          <Link
            to="/historico"
            className="hidden sm:inline-flex items-center justify-center rounded-full border border-border p-2 text-muted-foreground hover:text-foreground hover:border-amber-400 transition"
            title="Meu histórico"
            aria-label="Meu histórico"
          >
            <History className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Meu histórico</span>
          </Link>
          <ThemeToggle />
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-muted-foreground"
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="container mx-auto px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block py-3 text-base font-semibold"
            >
              Início
            </Link>
            <div>
              <Link
                to="/ferramentas"
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-base font-semibold"
              >
                Ferramentas →
              </Link>
              <div className="ml-3 grid grid-cols-1">
                {featuredTools.slice(0, 6).map((t) => (
                  <Link
                    key={t.slug}
                    to="/ferramentas/$slug"
                    params={{ slug: t.slug }}
                    onClick={() => setMobileOpen(false)}
                    className="py-2.5 text-sm text-muted-foreground"
                  >
                    {t.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <Link
                to="/artigos"
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-base font-semibold"
              >
                Artigos →
              </Link>
              <div className="ml-3 grid grid-cols-1">
                {featuredArticles.slice(0, 5).map((a) => (
                  <Link
                    key={a.slug}
                    to="/artigos/$slug"
                    params={{ slug: a.slug }}
                    onClick={() => setMobileOpen(false)}
                    className="py-2.5 text-sm text-muted-foreground"
                  >
                    {a.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/contato"
              onClick={() => setMobileOpen(false)}
              className="block py-3 text-base font-semibold"
            >
              Contato
            </Link>
            <div className="border-t border-border pt-3 mt-2 space-y-0.5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground pb-1">
                Suas ferramentas
              </p>
              <Link
                to="/marca"
                onClick={() => setMobileOpen(false)}
                className="block py-2.5 text-sm"
              >
                Sua marca nos PDFs
              </Link>
              <Link
                to="/historico"
                onClick={() => setMobileOpen(false)}
                className="block py-2.5 text-sm"
              >
                Meu histórico
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
